import classnames from 'classnames'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Popover } from 'reactstrap'
import { isEqual } from 'lodash'

import WaitlistActionModal from './modals/WaitlistActionModal'
import AddStudentForm from './forms/AddStudentForm'
import InviteToRegisterForm from './forms/InviteToRegisterForm'
import MoveToAnotherProgramForm from './forms/MoveToAnotherProgramForm'
import RemoveStudentForm from './forms/RemoveStudentForm'
import ProfileOverview from './ProfileOverview'
import WaitlistProgramSelectorModal from './modals/WaitlistProgramSelectorModal'

import { checkLicensePermission } from '../../../../../utility/communityLicense'
import * as Constants from '../../../../../misc/Constants'
import { PAYMENT_METHODS } from '../../../../../constants/program-constants'

import { ReactComponent as EllipsisIcon } from '../../../../../assets/images/common/icons/ellipsis-icon.svg'
import { ReactComponent as WarningIcon } from '../../../../../assets/images/common/icons/warning-icon.svg'

import classes from './WaitlistActions.module.scss'

const ACTIONS = {
  ADD_STUDENT: 'ADD_STUDENT',
  INVITE_TO_REGISTER: 'INVITE_TO_REGISTER',
  REMOVE_STUDENT: 'REMOVE_STUDENT',
}

const WaitlistActions = ({ waitlistEntry }) => {
  const course = useSelector(state => state.programDashboard.program)
  const licenseType = useSelector(state => state.user.current_user.communities[0].license_type)

  const [action, setAction] = useState(null)
  const [showPopover, setShowPopover] = useState(false)

  // 'Move To Another Program' states
  const [isProgramSelectorOpen, setIsProgramSelectorOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)

  const clearMoveStates = () => {
    setIsProgramSelectorOpen(false)
    setSelectedCourse(null)
  }
  const canAddToProgram = !checkLicensePermission(licenseType, [Constants.LICENSES.REVENUE_SHARE])
    && !isEqual(course.paymentMethod, PAYMENT_METHODS.CARD_ONLY)

  return (
    <div id='WaitlistActions' className={classes.wrapper}>
      <button
        id={`ellipsis-button-${waitlistEntry.id}`}
        className={classnames('w-100 d-flex justify-content-center align-items-center', classes.ellipsisButton)}
      >
        <EllipsisIcon className={classes.icon} />
      </button>
      <Popover
        placement='bottom'
        target={`ellipsis-button-${waitlistEntry.id}`}
        isOpen={showPopover}
        toggle={() => setShowPopover(prev => !prev)}
        popperClassName='popover-style'
        trigger='legacy'
      >
        <div className={classnames('d-flex flex-column align-items-start', classes.actions)}>
          {canAddToProgram && (
            <button
              onClick={() => { setAction(ACTIONS.ADD_STUDENT); setShowPopover(false) }}
            >
              Add to Program
            </button>
          )}
          <button onClick={() => { setAction(ACTIONS.INVITE_TO_REGISTER); setShowPopover(false) }}>
            {waitlistEntry.courseInvitationStatus !== 'not_found' ? 'Resend' : 'Send'} Registration Link
          </button>
          <button onClick={() => { setIsProgramSelectorOpen(true); setShowPopover(false) }}>
            Move to Another Program
          </button>
          <Link
            to='/announcements/new-campaign'
            state={{ smartToggle: 'email', students: [waitlistEntry.user] }}
          >
            Send an Email
          </Link>
          <button
            className={classes.danger}
            onClick={() => { setAction(ACTIONS.REMOVE_STUDENT); setShowPopover(false) }}
          >
            Remove Student
          </button>
        </div>
      </Popover>

      {action === ACTIONS.ADD_STUDENT && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.ADD_STUDENT}
          onClose={() => setAction(null)}
          student={waitlistEntry.user}
          title='Add to Program'
          subtitle='Add this student to the program, they will receive an email.'
          form={
            <AddStudentForm
              course={course}
              waitlistEntry={waitlistEntry}
              closeModal={() => setAction(null)}
            />
          }
        />
      )}

      {action === ACTIONS.INVITE_TO_REGISTER && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.INVITE_TO_REGISTER}
          onClose={() => setAction(null)}
          student={waitlistEntry.user}
          title='Send Registration Link'
          subtitle='Send Registration Link to this Student.'
          subHeaderSection={
            <div className='d-flex align-items-center mb-4'>
              <WarningIcon className={classes.expiryWarningIcon} />
              <p className={classes.expiryWarningText}>Registration Links expire after 48 hrs.</p>
            </div>
          }
          form={
            <InviteToRegisterForm
              course={course}
              waitlistEntry={waitlistEntry}
              closeModal={() => setAction(null)}
            />
          }
        />
      )}

      {isProgramSelectorOpen && (
        <WaitlistProgramSelectorModal
          title='Move Student from Waitlist'
          isOpen={isProgramSelectorOpen}
          currentProgram={course}
          selectedProgram={selectedCourse}
          onSelectProgram={setSelectedCourse}
          selectedStudent={waitlistEntry.user}
          onClose={() => { setIsProgramSelectorOpen(false); clearMoveStates() }}
          onProceed={() => { setAction(ACTIONS.MOVE_TO_ANOTHER_PROGRAM); setIsProgramSelectorOpen(false) }}
        />
      )}

      {action === ACTIONS.MOVE_TO_ANOTHER_PROGRAM && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.MOVE_TO_ANOTHER_PROGRAM}
          onClose={() => { setAction(null); clearMoveStates() }}
          student={waitlistEntry.user}
          title='Confirm Move from Waitlist'
          subtitle='Review and confirm before enrolling this student in a new program.'
          customInfoPanel={
            <>
              <ProfileOverview student={waitlistEntry.user} />
              <div className={classes.divider} />
              <div className='my-2'>
                <p className='font-weight-bolder mb-0'>From (Waitlist)</p>
                <span>{course.name}</span>
              </div>
              <div className='mb-2'>
                <p className='font-weight-bolder mb-0'>To (New Program)</p>
                <span>{selectedCourse?.name || ''}</span>
              </div>
            </>
          }
          form={
            <MoveToAnotherProgramForm
              course={course}
              selectedCourse={selectedCourse}
              waitlistEntry={waitlistEntry}
              closeModal={() => { setAction(null); clearMoveStates() }}
            />
          }
        />
      )}

      {action === ACTIONS.REMOVE_STUDENT && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.REMOVE_STUDENT}
          onClose={() => setAction(null)}
          student={waitlistEntry.user}
          title='Remove from Waitlist'
          subtitle='Remove this student to the program, they will receive an email.'
          form={
            <RemoveStudentForm
              course={course}
              waitlistEntry={waitlistEntry}
              closeModal={() => setAction(null)}
            />
          }
        />
      )}
    </div>
  )
}

WaitlistActions.propTypes = {
  waitlistEntry: PropTypes.shape({
    id: PropTypes.number,
    courseInvitationStatus: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      invited: PropTypes.bool,
    }),
  }),
}

export default WaitlistActions
